import { useState } from 'react';

import { Button, Dropdown, Multiselect, TextInput } from 'fluxo-ui';
import { FilterIcon, SearchIcon, TimesIcon } from 'fluxo-ui/icons';

import { QUESTION_TYPES } from '../../common/constants';

import type { ComponentEvent, ListItem } from 'fluxo-ui';

interface SearchFilters {
    query?: string;
    types?: string[];
    categories?: string[];
    difficulty?: string;
    status?: string;
    createdBy?: string;
}

interface QuestionSearchProps {
    onSearch?: (filters: SearchFilters) => void;
    loading?: boolean;
    showAdvanced?: boolean;
}

const QuestionSearch: React.FC<QuestionSearchProps> = ({ onSearch, loading = false, showAdvanced = true }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState<SearchFilters>({});

    const questionTypes: ListItem[] = [
        { label: 'Single Choice', value: QUESTION_TYPES.MCSA },
        { label: 'Multiple Choice', value: QUESTION_TYPES.MCMA },
        { label: 'True/False', value: QUESTION_TYPES.TRUE_FALSE },
        { label: 'Fill Blanks', value: QUESTION_TYPES.FILL_BLANKS },
        { label: 'Short Answer', value: QUESTION_TYPES.SHORT_ANSWER },
        { label: 'Long Answer', value: QUESTION_TYPES.LONG_ANSWER },
        { label: 'Code', value: QUESTION_TYPES.CODE },
    ];

    const difficulties: ListItem[] = [
        { label: 'Beginner', value: 'beginner' },
        { label: 'Easy', value: 'easy' },
        { label: 'Intermediate', value: 'intermediate' },
        { label: 'Hard', value: 'hard' },
        { label: 'Expert', value: 'expert' },
    ];

    const statuses: ListItem[] = [
        { label: 'All', value: '' },
        { label: 'Draft', value: 'draft' },
        { label: 'Pending Review', value: 'pending_review' },
        { label: 'Approved', value: 'approved' },
        { label: 'Rejected', value: 'rejected' },
    ];

    // Mock categories - in real app, fetch from API
    const categories: ListItem[] = [
        { label: 'JavaScript', value: 'javascript' },
        { label: 'React', value: 'react' },
        { label: 'TypeScript', value: 'typescript' },
        { label: 'Node.js', value: 'nodejs' },
        { label: 'Python', value: 'python' },
        { label: 'General Knowledge', value: 'general' },
    ];

    const handleSearch = () => {
        onSearch?.({
            query: searchQuery.trim() || undefined,
            ...filters,
        });
    };

    const handleClearFilters = () => {
        setSearchQuery('');
        setFilters({});
        onSearch?.({});
    };

    const updateFilter = <K extends keyof SearchFilters>(key: K, value: SearchFilters[K]) => {
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);
    };

    const activeFilterCount = Object.values(filters).filter((v) => v && (Array.isArray(v) ? v.length > 0 : true)).length;

    return (
        <div className="space-y-4">
            {/* Search Bar */}
            <div className="flex gap-3">
                <div className="flex-1">
                    <TextInput
                        value={searchQuery}
                        onChange={(e: ComponentEvent<string>) => setSearchQuery(e.value)}
                        placeholder="Search questions by text, tags, or ID..."
                        leftIcon={<SearchIcon className="w-5 h-5 text-slate-400" />}
                        onKeyPress={(e: any) => e.key === 'Enter' && handleSearch()}
                        disabled={loading}
                    />
                </div>
                <Button label="Search" variant="primary" onClick={handleSearch} isLoading={loading} disabled={loading} />
                {showAdvanced && (
                    <Button
                        label={`Filters ${activeFilterCount > 0 ? `(${activeFilterCount})` : ''}`}
                        leftIcon={FilterIcon}
                        variant="default"
                        layout={showFilters ? 'default' : 'outlined'}
                        onClick={() => setShowFilters(!showFilters)}
                    />
                )}
                {(searchQuery || activeFilterCount > 0) && (
                    <Button label="Clear" leftIcon={TimesIcon} variant="default" layout="outlined" onClick={handleClearFilters} />
                )}
            </div>

            {/* Advanced Filters */}
            {showFilters && showAdvanced && (
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Question Types */}
                        <div>
                            <label className="block text-sm font-medium text-slate-900 dark:text-white mb-2">Question Types</label>
                            <Multiselect
                                options={questionTypes}
                                value={filters.types || []}
                                onChange={(e: ComponentEvent<string[]>) => updateFilter('types', e.value)}
                                placeholder="All types"
                                searchable={false}
                            />
                        </div>

                        {/* Categories */}
                        <div>
                            <label className="block text-sm font-medium text-slate-900 dark:text-white mb-2">Categories</label>
                            <Multiselect
                                options={categories}
                                value={filters.categories || []}
                                onChange={(e: ComponentEvent<string[]>) => updateFilter('categories', e.value)}
                                placeholder="All categories"
                                searchable
                            />
                        </div>

                        {/* Difficulty */}
                        <div>
                            <label className="block text-sm font-medium text-slate-900 dark:text-white mb-2">Difficulty</label>
                            <Dropdown
                                options={[{ label: 'All Levels', value: '' }, ...difficulties]}
                                value={filters.difficulty || ''}
                                onChange={(e: ComponentEvent<string>) => updateFilter('difficulty', e.value)}
                                placeholder="Select difficulty"
                            />
                        </div>

                        {/* Status */}
                        <div>
                            <label className="block text-sm font-medium text-slate-900 dark:text-white mb-2">Status</label>
                            <Dropdown
                                options={statuses}
                                value={filters.status || ''}
                                onChange={(e: ComponentEvent<string>) => updateFilter('status', e.value)}
                                placeholder="Select status"
                            />
                        </div>

                        {/* Created By */}
                        <div>
                            <label className="block text-sm font-medium text-slate-900 dark:text-white mb-2">Created By</label>
                            <Dropdown
                                options={[
                                    { label: 'Anyone', value: '' },
                                    { label: 'Me', value: 'me' },
                                    { label: 'My Organization', value: 'org' },
                                ]}
                                value={filters.createdBy || ''}
                                onChange={(e: ComponentEvent<string>) => updateFilter('createdBy', e.value)}
                                placeholder="Select creator"
                            />
                        </div>
                    </div>

                    {/* Filter Actions */}
                    <div className="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-slate-700">
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                            {activeFilterCount > 0 ? (
                                <span>
                                    {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active
                                </span>
                            ) : (
                                <span>No filters applied</span>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button
                                label="Reset Filters"
                                variant="default"
                                size="sm"
                                layout="outlined"
                                onClick={() => setFilters({})}
                                disabled={activeFilterCount === 0}
                            />
                            <Button label="Apply Filters" variant="primary" size="sm" onClick={handleSearch} isLoading={loading} />
                        </div>
                    </div>
                </div>
            )}

            {/* Active Filters Display */}
            {activeFilterCount > 0 && !showFilters && (
                <div className="flex flex-wrap gap-2">
                    {filters.types && filters.types.length > 0 && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200">
                            Types: {filters.types.length}
                            <button onClick={() => updateFilter('types', [])} className="ml-2 hover:text-teal-900 dark:hover:text-teal-100">
                                <TimesIcon className="w-3 h-3" />
                            </button>
                        </span>
                    )}
                    {filters.categories && filters.categories.length > 0 && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                            Categories: {filters.categories.length}
                            <button
                                onClick={() => updateFilter('categories', [])}
                                className="ml-2 hover:text-green-900 dark:hover:text-green-100"
                            >
                                <TimesIcon className="w-3 h-3" />
                            </button>
                        </span>
                    )}
                    {filters.difficulty && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                            Difficulty: {difficulties.find((d) => d.value === filters.difficulty)?.label}
                            <button
                                onClick={() => updateFilter('difficulty', undefined)}
                                className="ml-2 hover:text-purple-900 dark:hover:text-purple-100"
                            >
                                <TimesIcon className="w-3 h-3" />
                            </button>
                        </span>
                    )}
                    {filters.status && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200">
                            Status: {statuses.find((s) => s.value === filters.status)?.label}
                            <button
                                onClick={() => updateFilter('status', undefined)}
                                className="ml-2 hover:text-orange-900 dark:hover:text-orange-100"
                            >
                                <TimesIcon className="w-3 h-3" />
                            </button>
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default QuestionSearch;
